import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import SiteFooter from "../../components/SiteFooter";
import { ArrowRight } from "lucide-react";
import BrandLogo from "../../components/BrandLogo";

export default function AgencyOnboarding() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    agency_name: user?.full_name || "",
    focus: "",
    team_size: "",
  });
  const [saving, setSaving] = useState(false);

  const upd = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const finish = async () => {
    setSaving(true);
    try {
      // Persist agency name on the user (simple; we don't have a dedicated agency-profile endpoint yet).
      await api.put("/profile", { full_name: form.agency_name, sport: form.focus, achievements: form.team_size ? `Team size: ${form.team_size}` : "" });
      await api.post("/onboarding/complete", { full_name: form.agency_name });
      await refresh();
      navigate("/app/dashboard");
    } finally { setSaving(false); }
  };

  return (
    <div className="min-h-screen bg-bg text-white flex flex-col">
      <div className="px-6 md:px-12 py-6 border-b border-line flex justify-between items-center">
        <BrandLogo variant="wordmark" size={28} />
        <div className="label-eyebrow">Agency setup</div>
      </div>

      <div className="flex-1 max-w-2xl w-full mx-auto px-6 md:px-12 py-12">
        <div className="label-eyebrow">For agents, managers & academies</div>
        <h1 className="font-heading text-4xl md:text-5xl uppercase tracking-tighter mt-3" data-testid="agency-onboarding-title">Set up your agency workspace.</h1>
        <p className="text-ink-muted mt-3">You'll be able to add athletes instantly, build their profiles, and run sponsorship outreach on their behalf — even before they join.</p>

        <div className="mt-10 space-y-5">
          <Field label="Agency / academy name *"><Input value={form.agency_name} onChange={v => upd("agency_name", v)} testid="agency-name" /></Field>
          <Field label="Primary focus / sport"><Input value={form.focus} onChange={v => upd("focus", v)} placeholder="Football, MMA, Multi-sport…" testid="agency-focus" /></Field>
          <Field label="How many athletes do you currently manage?"><Input value={form.team_size} onChange={v => upd("team_size", v)} placeholder="3-10, 10-50, 50+" testid="agency-size" /></Field>
        </div>

        <div className="mt-12 flex justify-end">
          <button onClick={finish} disabled={saving || !form.agency_name} className="btn-volt inline-flex items-center gap-2 disabled:opacity-40" data-testid="agency-onb-finish">
            {saving ? "Saving…" : "Enter workspace"} <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
      <SiteFooter />
    </div>
  );
}

function Field({ label, children }) { return <div><label className="label-eyebrow block mb-2">{label}</label>{children}</div>; }
function Input({ value, onChange, placeholder = "", testid }) {
  return <input value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} data-testid={testid}
    className="w-full bg-bg border border-line focus:border-volt outline-none px-4 py-3 text-white" />;
}
